import { forwardRef, useEffect, useId, useMemo, type ReactNode } from "react";
import { twJoin, twMerge } from "tailwind-merge";
import { Label } from "@components/Label/Label";
import { encodeBase64, formatImageSrc } from "@utils/common";

const checkedSvgIcon =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">\n' +
  '    <path d="M10 3L4.5 8.5L2 6" stroke="#094A1A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>\n' +
  "</svg>";

const checkedDisabledSvgIcon =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">\n' +
  '    <path d="M10 3L4.5 8.5L2 6" stroke="#7B8490" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>\n' +
  "</svg>";

const indeterminateSvgIcon =
  "<svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 16 16'><path stroke='#094A1A' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 8h8'/></svg>";

const indeterminateDisabledSvgIcon =
  "<svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 16 16'><path stroke='#7B8490' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 8h8'/></svg>";

const loaderIcon =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7B8490" ><g><path d="M12 3a9 9 0 0 1 9 9h-2a7 7 0 0 0-7-7V3z" /></g></svg>';

const checkedIconSrc = formatImageSrc({
  fileName: "",
  mimeType: "image/svg+xml",
  contentBase64: encodeBase64(checkedSvgIcon),
});

const checkedDisabledIconSrc = formatImageSrc({
  fileName: "",
  mimeType: "image/svg+xml",
  contentBase64: encodeBase64(checkedDisabledSvgIcon),
});

const indeterminateIconSrc = formatImageSrc({
  fileName: "",
  mimeType: "image/svg+xml",
  contentBase64: encodeBase64(indeterminateSvgIcon),
});

const indeterminateDisabledIconSrc = formatImageSrc({
  fileName: "",
  mimeType: "image/svg+xml",
  contentBase64: encodeBase64(indeterminateDisabledSvgIcon),
});

const loaderIconSrc = formatImageSrc({
  fileName: "",
  mimeType: "image/svg+xml",
  contentBase64: encodeBase64(loaderIcon),
});

export interface ICheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Input label text.
   */
  label?: ReactNode;
  /**
   * Error text.
   */
  error?: string;
  /**
   * Custom checked icon.
   */
  checkedSvgIcon?: string;
  /**
   * Whether checkbox loading.
   */
  isLoading?: boolean;
  /**
   * Class name for the label.
   */
  labelClassName?: string;
  /**
   * Class name for the wrapper.
   */
  wrapperClassName?: string;
  /**
   * Whether checkbox is indeterminate state.
   */
  isIndeterminate?: boolean;
}

// TODO: Keep an eye on HeadleesUI 2.0 as it will have more customizable, less hacky component
export const Checkbox = forwardRef<HTMLInputElement, ICheckboxProps>(
  (props, ref) => {
    const {
      label,
      className,
      labelClassName,
      wrapperClassName,
      error,
      disabled,
      checked,
      checkedSvgIcon: customCheckedSvgIcon,
      isLoading,
      isIndeterminate = false,
      ...rest
    } = props;
    const inputId = useId();

    const customCheckedSvgIconSrc = useMemo(
      () =>
        customCheckedSvgIcon &&
        formatImageSrc({
          fileName: "",
          mimeType: "image/svg+xml",
          contentBase64: encodeBase64(customCheckedSvgIcon),
        }),
      [customCheckedSvgIcon],
    );

    // eslint-disable-next-line
    const isDisabled = disabled || isLoading;

    useEffect(() => {
      const element = document.getElementById(inputId) as HTMLInputElement;

      if (element) {
        element.indeterminate = isIndeterminate;
      }
    }, [inputId, isIndeterminate, checked, disabled, isLoading]);

    return (
      <div className={twJoin("flex w-full flex-col gap-1.5", wrapperClassName)}>
        <div
          className={twJoin(
            "flex flex-row justify-items-start",
            label && "gap-2",
          )}
        >
          <input
            style={{
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              "--checked-icon-url": `url(${
                customCheckedSvgIconSrc ?? checkedIconSrc
              })`,
              "--checked-disabled-icon-url": `url(${checkedDisabledIconSrc})`,
              "--indeterminate-icon-url": `url(${indeterminateIconSrc})`,
              "--indeterminate-disabled-icon-url": `url(${indeterminateDisabledIconSrc})`,
              "--loader-icon-url": `url(${loaderIconSrc})`,
            }}
            id={inputId}
            className={twMerge(
              twJoin(
                "peer relative size-5 cursor-pointer appearance-none rounded border border-base-black transition",
                "checked:border-green-dark checked:bg-green-light checked:bg-none",
                "checked:before:absolute checked:before:inset-0 checked:before:bg-[image:var(--checked-icon-url)] checked:before:bg-[length:12px_12px] checked:before:bg-center checked:before:bg-no-repeat",
                "hover:border-base-black hover:bg-transparent",
                "checked:hover:border-green-dark checked:hover:bg-green-light",
                "focus:ring-base-black focus:ring-opacity-20 focus:ring-offset-0",
                "checked:focus:border-green-dark checked:focus:bg-green-light checked:focus:ring-green-dark checked:focus:ring-opacity-20",
                "checked:focus:before:absolute checked:focus:before:inset-0 checked:focus:before:bg-[image:var(--checked-icon-url)] checked:focus:before:bg-[length:12px_12px] checked:focus:before:bg-center checked:focus:before:bg-no-repeat",
                "disabled:cursor-default disabled:border-grey-dark disabled:bg-grey-light",
                "checked:disabled:border-grey-dark checked:disabled:bg-grey-light",
                "checked:disabled:before:bg-[image:var(--checked-disabled-icon-url)]",
                "indeterminate:border-green-dark indeterminate:bg-green-light indeterminate:bg-none",
                "indeterminate:before:absolute indeterminate:before:inset-0 indeterminate:before:bg-[image:var(--indeterminate-icon-url)] indeterminate:before:bg-center indeterminate:before:bg-no-repeat",
                "indeterminate:hover:border-green-dark indeterminate:hover:bg-green-light",
                "indeterminate:focus:border-green-dark indeterminate:focus:bg-green-light indeterminate:focus:ring-green-dark indeterminate:focus:ring-opacity-20",
                "indeterminate:disabled:border-grey-dark indeterminate:disabled:bg-grey-light",
                "indeterminate:disabled:before:bg-[image:var(--indeterminate-disabled-icon-url)]",
              ),
              isLoading &&
                "checked:disabled:before:absolute checked:disabled:before:inset-0 checked:disabled:before:animate-spin checked:disabled:before:bg-[image:var(--loader-icon-url)] checked:disabled:before:bg-[length:12px_12px] checked:disabled:before:bg-center checked:disabled:before:bg-no-repeat",
              isLoading &&
                "disabled:before:absolute disabled:before:inset-0 disabled:before:animate-spin disabled:before:bg-[image:var(--loader-icon-url)] disabled:before:bg-[length:12px_12px] disabled:before:bg-center disabled:before:bg-no-repeat",
              isLoading &&
                "indeterminate:disabled:before:absolute indeterminate:disabled:before:inset-0 indeterminate:disabled:before:animate-spin indeterminate:disabled:before:bg-[image:var(--loader-icon-url)] indeterminate:disabled:before:bg-[length:12px_12px] indeterminate:disabled:before:bg-center indeterminate:disabled:before:bg-no-repeat",
              className,
            )}
            checked={checked}
            ref={ref}
            {...rest}
            type="checkbox"
            disabled={isDisabled}
          />
          {label && (
            <Label
              htmlFor={inputId}
              className={twMerge(
                "select-none peer-disabled:cursor-default",
                disabled && "text-grey-dark",
                labelClassName,
              )}
            >
              {label}
            </Label>
          )}
        </div>
        {error && (
          <span className="break-all text-sm text-red-dark">{error}</span>
        )}
      </div>
    );
  },
);
